import { useSeoMeta } from '@unhead/react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNostr } from '@nostrify/react';
import { generateSecretKey, getPublicKey, finalizeEvent, type NostrEvent } from 'nostr-tools';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CashuWallet } from '@/components/CashuWallet';
import { useCashu } from '@/contexts/CashuContext';
import { useToast } from '@/hooks/useToast';
import { Send, SkipForward, Play, Users, Sparkles, Coins } from 'lucide-react';

// Custom kind for anonymous chat coordination
const CHAT_QUEUE_KIND = 38383; // Queue presence announcement
const CHAT_MESSAGE_KIND = 14; // Direct message kind (ephemeral)

type ChatState = 'idle' | 'waiting' | 'connected' | 'disconnected';

interface Message {
  id: string;
  content: string;
  sender: 'you' | 'stranger';
  timestamp: number;
  tip?: number; // Optional tip amount
}

const Index = () => {
  useSeoMeta({
    title: 'nOMEGLEstr - Anonymous Chat',
    description: 'Talk to random strangers anonymously using Nostr',
  });

  const { nostr } = useNostr();
  const { sendTokens } = useCashu();
  const { toast } = useToast();
  const [state, setState] = useState<ChatState>('idle');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [mySecretKey] = useState(() => generateSecretKey());
  const [myPubkey] = useState(() => getPublicKey(mySecretKey));
  const [partnerPubkey, setPartnerPubkey] = useState<string | null>(null);
  const [onlineCount, setOnlineCount] = useState<number>(0);
  const [connectionError, setConnectionError] = useState<boolean>(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const lastMessageTimestamp = useRef<number>(Math.floor(Date.now() / 1000));
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const onlineCountInterval = useRef<NodeJS.Timeout | null>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollEl = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollEl) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    }
  }, [messages]);

  // Poll for messages when connected to a partner
  useEffect(() => {
    if (!partnerPubkey || state !== 'connected') return;

    const pollMessages = async () => {
      try {
        const events = await nostr.query([{
          kinds: [CHAT_MESSAGE_KIND],
          '#p': [myPubkey],
          authors: [partnerPubkey],
          since: lastMessageTimestamp.current,
          limit: 50,
        }]);

        events.forEach((event: NostrEvent) => {
          // Update timestamp first to avoid re-querying
          if (event.created_at > lastMessageTimestamp.current) {
            lastMessageTimestamp.current = event.created_at;
          }

          if (event.content === 'DISCONNECTED') {
            setState('disconnected');
            addSystemMessage('Stranger disconnected.');
            setPartnerPubkey(null);
            return;
          }

          if (event.content.startsWith('CONNECTED:')) {
            return; // Already handled
          }

          // Check if this is a tip message
          if (event.content.startsWith('TIP:')) {
            const parts = event.content.split(':');
            if (parts.length === 3) {
              const amount = parseInt(parts[1]);
              const token = parts[2];
              
              // Automatically receive the tip
              addSystemMessage(`Stranger sent you ${amount} sats! ✨`);
              return;
            }
          }

          // Regular message
          const message: Message = {
            id: event.id,
            content: event.content,
            sender: 'stranger',
            timestamp: event.created_at,
          };

          setMessages(prev => {
            if (prev.some(m => m.id === message.id)) return prev;
            return [...prev, message];
          });
        });
      } catch (error) {
        console.error('Error polling messages:', error);
      }
    };

    // Poll every 2 seconds
    pollingInterval.current = setInterval(pollMessages, 2000);
    pollMessages(); // Initial poll

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [partnerPubkey, state, myPubkey, nostr]);

  // Poll for online user count and test connection
  useEffect(() => {
    const updateOnlineCount = async () => {
      try {
        // Query for recent queue events (last 60 seconds)
        const recentEvents = await nostr.query([{
          kinds: [CHAT_QUEUE_KIND],
          since: Math.floor(Date.now() / 1000) - 60,
          limit: 100,
        }]);

        // Count unique pubkeys
        const uniqueUsers = new Set(recentEvents.map(e => e.pubkey));
        setOnlineCount(uniqueUsers.size);
        setConnectionError(false); // Connection is working
      } catch (error) {
        console.error('Error fetching online count:', error);
        setConnectionError(true); // Connection failed
      }
    };

    // Update immediately
    updateOnlineCount();

    // Then poll every 10 seconds
    onlineCountInterval.current = setInterval(updateOnlineCount, 10000);

    return () => {
      if (onlineCountInterval.current) {
        clearInterval(onlineCountInterval.current);
      }
    };
  }, [nostr]);

  // Start searching for a partner
  const startChat = async () => {
    setState('waiting');
    setMessages([]);
    setPartnerPubkey(null);
    // Set timestamp to 5 seconds ago to catch any incoming messages
    lastMessageTimestamp.current = Math.floor(Date.now() / 1000) - 5;

    try {
      // First, look for someone else waiting
      const waitingEvents = await nostr.query([{
        kinds: [CHAT_QUEUE_KIND],
        since: Math.floor(Date.now() / 1000) - 30, // Last 30 seconds
        limit: 50,
      }]);

      // Filter out our own announcements
      const others = waitingEvents.filter(e => e.pubkey !== myPubkey);

      if (others.length > 0) {
        // Pick a random person
        const randomPerson = others[Math.floor(Math.random() * others.length)];
        
        // Send a "connected" notification to partner FIRST
        await sendConnectedNotification(randomPerson.pubkey);
        
        // Then set our state (this will trigger message polling)
        setPartnerPubkey(randomPerson.pubkey);
        setState('connected');
        addSystemMessage('Connected to a stranger! 🎭');
        
        return;
      }

      // No one waiting, announce our presence
      const announcement = finalizeEvent({
        kind: CHAT_QUEUE_KIND,
        tags: [],
        content: 'waiting',
        created_at: Math.floor(Date.now() / 1000),
      }, mySecretKey);

      await nostr.event(announcement);

      // Poll for connection responses
      const waitForConnection = async () => {
        const events = await nostr.query([{
          kinds: [CHAT_MESSAGE_KIND],
          '#p': [myPubkey],
          since: lastMessageTimestamp.current,
          limit: 10,
        }]);

        for (const event of events) {
          if (event.content.startsWith('CONNECTED:')) {
            const partner = event.pubkey;
            setPartnerPubkey(partner);
            setState('connected');
            addSystemMessage('Connected to a stranger! 🎭');
            return true;
          }
        }
        return false;
      };

      // Check immediately first
      const immediateCheck = await waitForConnection();
      if (immediateCheck) return;

      // Poll for up to 30 seconds
      let attempts = 0;
      const maxAttempts = 15;
      
      const checkInterval = setInterval(async () => {
        attempts++;
        const connected = await waitForConnection();
        
        if (connected || attempts >= maxAttempts) {
          clearInterval(checkInterval);
          if (!connected) {
            setState('idle');
            addSystemMessage('No one available. Try again! 🔄');
          }
        }
      }, 2000);
    } catch (error) {
      console.error('Error starting chat:', error);
      setState('idle');
      setConnectionError(true);
      addSystemMessage('Connection error. Please check your internet connection.');
    }
  };

  const sendConnectedNotification = async (targetPubkey: string) => {
    const connectedMsg = finalizeEvent({
      kind: CHAT_MESSAGE_KIND,
      tags: [['p', targetPubkey]],
      content: `CONNECTED:${myPubkey}`,
      created_at: Math.floor(Date.now() / 1000),
    }, mySecretKey);

    await nostr.event(connectedMsg);
  };

  const nextChat = async () => {
    if (partnerPubkey) {
      // Send disconnect notification
      const disconnectMsg = finalizeEvent({
        kind: CHAT_MESSAGE_KIND,
        tags: [['p', partnerPubkey]],
        content: 'DISCONNECTED',
        created_at: Math.floor(Date.now() / 1000),
      }, mySecretKey);
      
      await nostr.event(disconnectMsg);
    }

    setState('idle');
    setMessages([]);
    setPartnerPubkey(null);
    
    // Automatically start new search
    setTimeout(() => startChat(), 100);
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || !partnerPubkey || state !== 'connected') return;

    const content = inputValue.trim();
    
    // Add to UI immediately
    const message: Message = {
      id: `local-${Date.now()}`,
      content,
      sender: 'you',
      timestamp: Math.floor(Date.now() / 1000),
    };
    setMessages(prev => [...prev, message]);
    setInputValue('');

    // Send to partner
    const chatMsg = finalizeEvent({
      kind: CHAT_MESSAGE_KIND,
      tags: [['p', partnerPubkey]],
      content,
      created_at: Math.floor(Date.now() / 1000),
    }, mySecretKey);

    await nostr.event(chatMsg);
  };

  const sendTip = async (amount: number) => {
    if (!partnerPubkey || state !== 'connected') return;

    try {
      const token = await sendTokens(amount);
      
      // Send tip message to partner
      const tipMsg = finalizeEvent({
        kind: CHAT_MESSAGE_KIND,
        tags: [['p', partnerPubkey]],
        content: `TIP:${amount}:${token}`,
        created_at: Math.floor(Date.now() / 1000),
      }, mySecretKey);

      await nostr.event(tipMsg);

      // Add to UI
      addSystemMessage(`You sent ${amount} sats to stranger! 💰`);

      toast({
        title: 'Tip Sent!',
        description: `You sent ${amount} sats to the stranger`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send tip',
        variant: 'destructive',
      });
    }
  };

  const addSystemMessage = (content: string) => {
    const message: Message = {
      id: `system-${Date.now()}`,
      content,
      sender: 'you',
      timestamp: Math.floor(Date.now() / 1000),
    };
    setMessages(prev => [...prev, message]);
  };

  const getStatusText = () => {
    switch (state) {
      case 'waiting':
        return 'Looking for someone...';
      case 'connected':
        return 'Connected';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Ready to connect';
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="min-h-screen flex flex-col items-center justify-center p-4 md:p-8">
        {/* Header */}
        <div className="w-full max-w-4xl mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Sparkles className="h-10 w-10 text-white drop-shadow-lg" />
                <div className="absolute inset-0 blur-xl bg-white/50" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg tracking-tight">
                  nOMEGLEstr
                </h1>
                <p className="text-white/80 text-sm">Anonymous chats, powered by Nostr</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                <Users className="h-4 w-4 text-white" />
                <span className="text-white font-semibold text-sm">{onlineCount}</span>
              </div>
              <CashuWallet />
            </div>
          </div>
        </div>

        {/* Main Chat Card */}
        <Card className="w-full max-w-4xl h-[600px] md:h-[700px] flex flex-col bg-white/95 backdrop-blur-xl shadow-2xl border-0 overflow-hidden">
          {/* Status Bar */}
          <div className={`px-6 py-4 border-b transition-colors ${
            state === 'connected' 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200/50' 
              : state === 'waiting'
              ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200/50'
              : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200/50'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  state === 'connected' ? 'bg-green-500 animate-pulse' :
                  state === 'waiting' ? 'bg-yellow-500 animate-pulse' :
                  'bg-gray-400'
                }`} />
                <span className="font-semibold text-gray-900">
                  {getStatusText()}
                </span>
              </div>
              
              {state === 'connected' && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => sendTip(100)}
                    className="gap-2"
                  >
                    <Coins className="h-4 w-4" />
                    Tip 100
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => sendTip(500)}
                    className="gap-2"
                  >
                    <Coins className="h-4 w-4" />
                    Tip 500
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Connection Error Warning */}
          {connectionError && (
            <div className="px-4 py-3 bg-red-50 border-b border-red-200 text-red-800 text-xs">
              <div className="flex items-center justify-center gap-2">
                <span className="font-semibold">⚠️ Connection Issue</span>
              </div>
              <div className="mt-1 text-center">
                Unable to connect to Nostr relays. If using Brave browser, please disable Shields.
              </div>
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 p-6" ref={scrollAreaRef}>
            <div className="space-y-4">
              {messages.length === 0 && state === 'idle' && (
                <div className="flex flex-col items-center justify-center h-full py-20 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                    <Sparkles className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Ready to meet someone new?
                  </h3>
                  <p className="text-gray-600 max-w-md">
                    Click "Start Chat" to be randomly matched with a stranger. 
                    You can also tip them sats using your Cashu wallet! 💰
                  </p>
                </div>
              )}
              {messages.map((msg) => {
                const isSystem = msg.content.includes('Connected') || 
                                msg.content.includes('disconnected') || 
                                msg.content.includes('available') ||
                                msg.content.includes('sent you') ||
                                msg.content.includes('You sent');
                
                if (isSystem) {
                  return (
                    <div key={msg.id} className="flex items-center justify-center">
                      <div className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">
                        {msg.content}
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={msg.id} className={`flex ${msg.sender === 'you' ? 'justify-end' : 'justify-start'}`}>
                    <div className="flex flex-col max-w-[75%]">
                      <div className={`text-xs font-semibold mb-1.5 ${
                        msg.sender === 'you' ? 'text-right text-purple-700' : 'text-gray-600'
                      }`}>
                        {msg.sender === 'you' ? 'You' : 'Stranger'}
                      </div>
                      <div className={`px-5 py-3 rounded-2xl shadow-sm ${
                        msg.sender === 'you'
                          ? 'bg-gradient-to-br from-violet-600 to-purple-600 text-white rounded-br-md'
                          : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
                      }`}>
                        <p className="leading-relaxed">{msg.content}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Input Area */}
          <div className="p-4 border-t bg-gray-50/50 backdrop-blur-sm">
            <div className="flex gap-2 mb-3">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={state === 'connected' ? "Type your message..." : "Start a chat to send messages"}
                disabled={state !== 'connected'}
                maxLength={500}
                className="flex-1 bg-white border-gray-200 focus-visible:ring-purple-500"
              />
              <Button
                onClick={sendMessage}
                disabled={state !== 'connected' || !inputValue.trim()}
                size="lg"
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-md"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>

            {/* Controls */}
            <div className="flex gap-2 justify-center">
              {state === 'idle' && (
                <Button
                  onClick={startChat}
                  size="lg"
                  className="flex-1 max-w-md bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-md gap-2"
                >
                  <Play className="h-5 w-5" />
                  Start Chat
                </Button>
              )}
              {state === 'disconnected' && (
                <Button
                  onClick={startChat}
                  size="lg"
                  className="flex-1 max-w-md bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-md gap-2"
                >
                  <Play className="h-5 w-5" />
                  Start New Chat
                </Button>
              )}
              {(state === 'connected' || state === 'waiting') && (
                <Button
                  onClick={nextChat}
                  size="lg"
                  variant="destructive"
                  className="flex-1 max-w-md shadow-md gap-2"
                >
                  <SkipForward className="h-5 w-5" />
                  Next
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center">
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/70 hover:text-white text-sm font-medium transition-colors inline-flex items-center gap-2"
          >
            <Sparkles className="h-4 w-4" />
            Vibed with Shakespeare
          </a>
        </div>
      </div>
    </div>
  );
};

export default Index;
